import type { CompanyProfile, PostcodeAddress, PostcodeLookupOutcome, PublicForm, SubmitInput } from '@jfh/intake-shared';

/**
 * API origin. In dev, Vite proxies `/api` to the Worker, so an empty base works.
 * In production the renderer is served from a different origin (Pages), so it
 * calls the Worker directly — the Worker allows CORS for the public routes.
 * Override with VITE_API_BASE at build time.
 */
const API_BASE: string =
  import.meta.env.VITE_API_BASE ??
  (import.meta.env.PROD ? 'https://jfh-intake-api.jfhhorticultural.workers.dev' : '');

/** Response from the submit endpoint on success (HTTP 201). */
export interface SubmitResult {
  id: string;
  status: string;
}

/** A loaded form, or a structured failure the UI can render. */
export type FormLoad =
  | { kind: 'ok'; form: PublicForm }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

/** Outcome of a submit attempt. */
export type SubmitOutcome =
  | { kind: 'ok'; result: SubmitResult }
  | { kind: 'validation'; details: string[] }
  | { kind: 'error'; message: string };

export async function loadForm(slug: string): Promise<FormLoad> {
  try {
    const res = await fetch(
      `${API_BASE}/api/public/forms/${encodeURIComponent(slug)}`,
      { headers: { accept: 'application/json' } },
    );
    if (res.status === 404) return { kind: 'not_found' };
    if (!res.ok) {
      return { kind: 'error', message: `Request failed (${res.status}).` };
    }
    const form = (await res.json()) as PublicForm;
    return { kind: 'ok', form };
  } catch {
    return {
      kind: 'error',
      message: 'Could not reach the server. Please try again.',
    };
  }
}

export async function submitForm(
  slug: string,
  input: SubmitInput,
): Promise<SubmitOutcome> {
  try {
    const res = await fetch(
      `${API_BASE}/api/public/forms/${encodeURIComponent(slug)}/submit`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
    if (res.status === 422) {
      const body = (await res.json().catch(() => null)) as {
        details?: string[];
      } | null;
      return {
        kind: 'validation',
        details: body?.details ?? ['Validation failed.'],
      };
    }
    if (!res.ok) {
      return {
        kind: 'error',
        message: `Submission failed (${res.status}). Please try again.`,
      };
    }
    const result = (await res.json()) as SubmitResult;
    return { kind: 'ok', result };
  } catch {
    return {
      kind: 'error',
      message: 'Could not reach the server. Please try again.',
    };
  }
}

/** Result of a Companies House lookup for a company-number field (F8). */
export type CompanyLookupOutcome =
  | { kind: 'found'; company: CompanyProfile }
  | { kind: 'not_found' }
  | { kind: 'busy' }
  | { kind: 'unavailable' };

/** Look a UK postcode up for an address question (23/10/2026). */
export async function lookupPostcode(postcode: string): Promise<PostcodeLookupOutcome> {
  return postcodeOutcome(() =>
    fetch(`${API_BASE}/api/public/postcodes/${encodeURIComponent(postcode)}`, { headers: { accept: 'application/json' } }),
  );
}

/**
 * A postcode look-up's answer as an outcome. Shared with the portal, which
 * sends the request through its own /api proxy.
 */
export async function postcodeOutcome(request: () => Promise<Response>): Promise<PostcodeLookupOutcome> {
  try {
    const res = await request();
    if (res.status === 400) return { kind: 'invalid' };
    if (res.status === 404) return { kind: 'not_found' };
    if (res.status === 429) return { kind: 'busy' };
    if (!res.ok) return { kind: 'unavailable' };
    const body = (await res.json()) as { postcode: string; addresses: PostcodeAddress[] };
    return body.addresses?.length ? { kind: 'found', postcode: body.postcode, addresses: body.addresses } : { kind: 'not_found' };
  } catch {
    return { kind: 'unavailable' };
  }
}

export async function lookupCompany(number: string): Promise<CompanyLookupOutcome> {
  try {
    const res = await fetch(`${API_BASE}/api/public/companies/${encodeURIComponent(number)}`, {
      headers: { accept: 'application/json' },
    });
    if (res.status === 404 || res.status === 400) return { kind: 'not_found' };
    if (res.status === 429) return { kind: 'busy' };
    if (!res.ok) return { kind: 'unavailable' };
    const body = (await res.json()) as { company: CompanyProfile };
    return { kind: 'found', company: body.company };
  } catch {
    return { kind: 'unavailable' };
  }
}
